import React from 'react';
import { FileText, DollarSign, Users, Receipt, Globe, Calendar, TrendingUp } from 'lucide-react';
import { ReportTemplate } from '../../types/reports.types';
import { cn } from '../../lib/utils';

interface ReportTemplateCardProps {
  template: ReportTemplate;
  selected: boolean;
  onClick: () => void;
}

const iconMap: Record<string, React.ReactNode> = {
  network: <TrendingUp className="w-6 h-6" />,
  'dollar-sign': <DollarSign className="w-6 h-6" />,
  users: <Users className="w-6 h-6" />,
  receipt: <Receipt className="w-6 h-6" />,
  globe: <Globe className="w-6 h-6" />,
  calendar: <Calendar className="w-6 h-6" />,
};

export const ReportTemplateCard: React.FC<ReportTemplateCardProps> = React.memo(({ template, selected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full p-6 rounded-lg border-2 transition-all duration-200 text-left',
        'hover:shadow-md hover:scale-[1.02]',
        selected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950 dark:border-blue-600'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
      )}
    >
      <div className="flex items-start gap-4">
        <div className={cn(
          'p-3 rounded-lg',
          selected ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
        )}>
          {iconMap[template.icon] || <FileText className="w-6 h-6" />}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">
            {template.name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {template.description}
          </p>
          <div className="mt-3 flex flex-wrap gap-1">
            {template.defaultMetrics.slice(0, 3).map(metric => (
              <span
                key={metric}
                className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                {metric}
              </span>
            ))}
            {template.defaultMetrics.length > 3 && (
              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                +{template.defaultMetrics.length - 3} more
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
});

ReportTemplateCard.displayName = 'ReportTemplateCard';
